import { BenchmarkProto } from "build-ts/metadata";
import { CommentaryProto, CommentaryProto_CommentaryConfig, InsightsProto } from "build-ts/analysis";
import { GenerateAnalysisTree_Request } from "build-ts/interface";
import { AnalysisTreeRequestType, GenerateSubTree_Request, SetCommentary_Request, SetInsights_Request, StatefulAnalysisTreeRequest, StatefulRequest, StatefulRequestType, UpdateNode_Request } from "build-ts/stateful_api";
import { ObservationEditProto } from "build-ts/analysis_plan";

export class StatefulRequestBuilder {
    private transactionId: string;
  
    constructor(transactionId: string) {
      this.transactionId = transactionId;
    }
  
    public createStatefulRequest(
      analysisTreeRequests: StatefulAnalysisTreeRequest[]
    ): StatefulRequest {
      return {
        request_type: StatefulRequestType.ANALYSIS_TREE_REQUEST,
        state_key: {
          transaction_id: this.transactionId,
        },
        client_tree_sessions: [],
        analysis_tree_requests: analysisTreeRequests,
        tisa_requests: []
      };
    }

    private statefulGetTreeRequest = StatefulAnalysisTreeRequest.create({
      type: AnalysisTreeRequestType.GET_TREE,
      get_tree: {
          include_nodes: true,
          include_commentary: true,
          include_node_metric_map: true,
      },
    });

    // Generate tree request
    public GenerateTreeRequest(analysisTreeRequest: GenerateAnalysisTree_Request, getTree: boolean = true): StatefulRequest {

        const statefulGenerateTreeRequest = StatefulAnalysisTreeRequest.create({
            type: AnalysisTreeRequestType.GENERATE_TREE,
            generate_tree: analysisTreeRequest,
        });
        
        if(getTree){
          return this.createStatefulRequest([statefulGenerateTreeRequest, this.statefulGetTreeRequest]);
        }
        return this.createStatefulRequest([statefulGenerateTreeRequest]);
    }
    
    //Save tree request
    public SaveTreeRequest(): StatefulRequest {
      const statefulSaveTreeRequest = StatefulAnalysisTreeRequest.create({
        type: AnalysisTreeRequestType.SAVE_TREE,
        save_tree: {},
      });

      return this.createStatefulRequest([statefulSaveTreeRequest]);
    }

    // Update node-label request
    public UpdateNodeRequest(updateNodeRequest: UpdateNode_Request): StatefulRequest {
      const statefulUpdateNodeRequest = StatefulAnalysisTreeRequest.create({
        type: AnalysisTreeRequestType.UPDATE_NODE,
        update_node: updateNodeRequest,
      });

      return this.createStatefulRequest([statefulUpdateNodeRequest]);
    }

    // Generate commentary request
    public GenerateCommentaryRequest(commentaryConfig: CommentaryProto_CommentaryConfig): StatefulRequest {

      const statefulCommentaryRequest = StatefulAnalysisTreeRequest.create({
        type: AnalysisTreeRequestType.GENERATE_COMMENTARY,
        generate_commentary: { 
            configuration: commentaryConfig 
        },
      });
  
      return this.createStatefulRequest([statefulCommentaryRequest]);
    }
  
    // Get analysis node request
    public GetNodeRequest(nodeId: string): StatefulRequest {
      const statefulGetNodeRequest = StatefulAnalysisTreeRequest.create({
        type: AnalysisTreeRequestType.GET_ANALYSIS_NODE,
        get_analysis_node: { node_id: nodeId, include_data: true },
      });
  
      return this.createStatefulRequest([statefulGetNodeRequest]);
    }
  
    // Load and get tree request
    public LoadTreeRequest(treeId: string): StatefulRequest {

      const statefulLoadTreeRequest = StatefulAnalysisTreeRequest.create({
        type: AnalysisTreeRequestType.LOAD_TREE,
        load_tree: { tree_id: treeId },
      });
  
      return this.createStatefulRequest([statefulLoadTreeRequest, this.statefulGetTreeRequest]);
    }

    // Save commentary request
    public SaveCommentaryRequest(commentary: string, conversationId: string): StatefulRequest {

        const statefulSaveCommentaryRequest = StatefulAnalysisTreeRequest.create({
            type: AnalysisTreeRequestType.SAVE_COMMENTARY,
            save_commentary: {
                conversation_id: conversationId,
                edited_commentary: commentary,
            },
        });

        return this.createStatefulRequest([statefulSaveCommentaryRequest]);
    }

    // Delete commentary request
    public DeleteCommentaryRequest(commentary: CommentaryProto): StatefulRequest {
        const statefulDeleteCommentaryRequest = StatefulAnalysisTreeRequest.create({
            type: AnalysisTreeRequestType.DELETE_COMMENTARY,
            delete_commentary: {
                conversation_id: commentary?.conversation_header?.id,
            },
        });

        return this.createStatefulRequest([statefulDeleteCommentaryRequest]);
    }

    // Generate sub tree request
    public GenerateSubTreeRequest(
        nodeId: string,
        edgeId: string,
        rowIndex: number,
        benchmark: BenchmarkProto | null
    ): StatefulRequest {
      const statefulGenerateSubTreeRequest = StatefulAnalysisTreeRequest.create({
          type: AnalysisTreeRequestType.GENERATE_SUB_TREE,
          generate_sub_tree: GenerateSubTree_Request.create({
              parent_node_id: nodeId,
              ...(benchmark ? {} : {edge_id: edgeId}),
              row_index: rowIndex,
              benchmark: benchmark ?? undefined,
              create_root_node_only: true,
          }),
      });
      return this.createStatefulRequest([statefulGenerateSubTreeRequest, this.statefulGetTreeRequest]);
    }

    // New generate sub tree request for mkg
    public  GenerateSubTreeRequestMKG(
      nodeId: string,
      rowIndex: number,
      observationEdit: ObservationEditProto[]
    ): StatefulRequest {

      const statefulGenerateSubTreeRequest = StatefulAnalysisTreeRequest.create({
        type: AnalysisTreeRequestType.GENERATE_SUB_TREE,
        generate_sub_tree: GenerateSubTree_Request.create({
          parent_node_id: nodeId,
          row_index: rowIndex,
          child_step_edits: observationEdit,
          create_root_node_only: true,
        }),
      });

      return this.createStatefulRequest([statefulGenerateSubTreeRequest, this.statefulGetTreeRequest]);
    };

    // Update tree name and description request
    public UpdateTreeHeaderRequest(name: string, description: string): StatefulRequest {
      const statefulUpdateTreeNameRequest = StatefulAnalysisTreeRequest.create({
          type: AnalysisTreeRequestType.UPDATE_TREE_HEADER,
          update_tree_header: {
            updated_name: name,
            updated_description: description,
          },
      });

      return this.createStatefulRequest([statefulUpdateTreeNameRequest]);
    }

    // Get tree request
    public GetTreeRequest(): StatefulRequest {
      return this.createStatefulRequest([this.statefulGetTreeRequest]);
    }

    // Set commentary request
    public SetCommentaryRequest(commentaries: CommentaryProto[])
    : StatefulAnalysisTreeRequest {
      const setCommentary = SetCommentary_Request.create({
        commentary: commentaries,
      });
      const statefulSetCommentaryRequest = StatefulAnalysisTreeRequest.create({
        type: AnalysisTreeRequestType.SET_COMMENTARY,
        set_commentary: setCommentary,
      });
      return statefulSetCommentaryRequest;
    }

    // Set QnA request
    public SetInsightsRequest(qnaList: InsightsProto[]): StatefulAnalysisTreeRequest {
      const setQnA = SetInsights_Request.create({
        insights: qnaList,
      });
      const statefulSetQnARequest = StatefulAnalysisTreeRequest.create({
        type: AnalysisTreeRequestType.SET_INSIGHTS,
        set_insights: setQnA,
      });
      return statefulSetQnARequest;
    }
  }
  